import React, { useEffect, useState } from "react";
import "./Auth.scss";
import HalfSplashScreen from "./HalfSplashScreen";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import Api from "../../api";
import { defaultUser } from "../../api/data/users";
import { decrypt } from "../../utils/encrypter";
import { defaultOrg } from "../../api/data/orgs";
import { capitalizeFirstLetter } from "../../utils/words";
import { DEFAULT_INITIAL_ROUTE } from "../../utils/constants";
import ErrorInfoBox from "../common/ErrorInfoBox";
import useLoader from "../../utils/useLoader";
import Button from "../common/Button";

function SignUp() {
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(defaultOrg);
  const [error, setError] = useState({ title: "", message: "" });
  const { isLoading, setIsLoading } = useLoader();

  useEffect(() => {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const code = params.get("code");

    if (!code) {
      navigate("/login");
      return;
    }

    const base64decryptedCode = atob(code);
    const decodedCode = decrypt(base64decryptedCode);
    const org = JSON.parse(decodedCode);
    setOrganization(org);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (values) => {
    const errors = {};
    if (!values.firstName) {
      errors.firstName = "First Name is required";
    } else if (values.firstName.length <= 1) {
      errors.firstName = "Must be a valid first name";
    }

    if (!values.lastName) {
      errors.lastName = "Last Name is required";
    } else if (values.lastName.length <= 1) {
      errors.lastName = "Must be a valid last name";
    }

    if (!values.email) {
      errors.email = "Email address is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      errors.email = "You have entered an invalid email address";
    }

    if (!values.password) {
      errors.password = "Password is required";
    } else if (!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/i.test(values.password)) {
      errors.password =
        "Your Password must contain at least one number and one uppercase and lowercase letter, and at least 8 or more characters";
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      ...defaultUser,
      password: "",
    },
    validate,
    onSubmit: async (values) => {
      setIsLoading(true);
      values.orgID = organization.id;
      const password = values.password;

      delete values.password;

      const response = await Api.Firebase.Auth.signup(
        values.email,
        password,
        values
      );
      if (response && !response.success) {
        setError(response.code);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);

      return navigate(DEFAULT_INITIAL_ROUTE);
    },
  });

  return (
    <div className="auth-login-container">
      <div className="auth-login-form">
        <h1>Sign Up - {capitalizeFirstLetter(organization.name)}</h1>
        <ErrorInfoBox title={error.title} message={error.message} />
        <form onSubmit={formik.handleSubmit}>
          <label htmlFor="firstName">
            First Name<span className="input-required">*</span>
          </label>
          <input
            id="firstName"
            name="firstName"
            type="firstName"
            disabled={isLoading}
            onChange={formik.handleChange}
            value={formik.values.firstName}
          />
          {formik.errors.firstName ? (
            <div className="input-error-message">{formik.errors.firstName}</div>
          ) : null}

          <label htmlFor="lastName">
            Last Name<span className="input-required">*</span>
          </label>
          <input
            id="lastName"
            name="lastName"
            type="lastName"
            disabled={isLoading}
            onChange={formik.handleChange}
            value={formik.values.lastName}
          />
          {formik.errors.lastName ? (
            <div className="input-error-message">{formik.errors.lastName}</div>
          ) : null}

          <label htmlFor="email">
            Email<span className="input-required">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            disabled={isLoading}
            onChange={formik.handleChange}
            value={formik.values.email}
          />
          {formik.errors.email ? (
            <div className="input-error-message">{formik.errors.email}</div>
          ) : null}

          <label htmlFor="password">
            Password<span className="input-required">*</span>
          </label>
          <input
            id="password"
            name="password"
            type="password"
            disabled={isLoading}
            onChange={formik.handleChange}
            value={formik.values.password}
          />
          {formik.errors.password ? (
            <div className="input-error-message">{formik.errors.password}</div>
          ) : null}

          <Button type="submit" isLoading={isLoading} label="Create Account" />
        </form>
      </div>
      <HalfSplashScreen />
    </div>
  );
}

export default SignUp;
